import React, { useEffect, useState } from "react";
import { db } from "./firebase";
import { collection, addDoc, query, where, getDocs, doc, updateDoc, arrayUnion, serverTimestamp } from "firebase/firestore";
import logo from "./logo.png";

function generateJoinCode() {
  return Math.random().toString(36).substring(2, 8).toUpperCase();
}

function Toggle({ label, description, value, onChange }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "12px 0", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
      <div style={{ flex: 1, marginRight: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: "white" }}>{label}</div>
        <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 2, lineHeight: 1.4 }}>{description}</div>
      </div>
      <button onClick={() => onChange(!value)} style={{ width: 48, height: 26, borderRadius: 13, border: "none", cursor: "pointer", background: value ? "#00C853" : "rgba(255,255,255,0.15)", position: "relative", transition: "background 0.2s", flexShrink: 0 }}>
        <div style={{ width: 20, height: 20, borderRadius: "50%", background: "white", position: "absolute", top: 3, left: value ? 25 : 3, transition: "left 0.2s", boxShadow: "0 1px 4px rgba(0,0,0,0.3)" }} />
      </button>
    </div>
  );
}

function ContestHome({ user, onEnterContest, autoJoinCode = "", defaultTab, onTabChange }) {
  const [myContests, setMyContests] = useState([]);
  const [newContestName, setNewContestName] = useState("");
  const [newContestMessage, setNewContestMessage] = useState("");
  const [allowMultipleEntries, setAllowMultipleEntries] = useState(true);
  const [openPool, setOpenPool] = useState(true);
  const [joinCode, setJoinCode] = useState(autoJoinCode);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [tab, setTabRaw] = useState(defaultTab || (autoJoinCode ? "join" : "my"));
  const setTab = (t) => { setTabRaw(t); if (onTabChange) onTabChange(t); };

  // When an auto join code arrives (e.g. after sign-in via invite link), fill it in and open the Join tab
  useEffect(() => {
    if (autoJoinCode) {
      setJoinCode(autoJoinCode);
      setTabRaw("join");
    }
  }, [autoJoinCode]);

  useEffect(() => { fetchMyContests(); }, []);

  const fetchMyContests = async () => {
    setLoading(true);
    const q = query(collection(db, "contests"), where("members", "array-contains", user.uid));
    const snap = await getDocs(q);
    setMyContests(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    setLoading(false);
  };

  const createContest = async () => {
    if (!newContestName.trim()) return;
    const code = generateJoinCode();
    const docRef = await addDoc(collection(db, "contests"), {
      name: newContestName.trim(), adminUid: user.uid, adminName: user.displayName,
      joinCode: code, picksLocked: false, allowMultipleEntries, openPool,
      message: newContestMessage.trim(), members: [user.uid], createdAt: serverTimestamp()
    });
    onEnterContest({ id: docRef.id, name: newContestName.trim(), adminUid: user.uid, joinCode: code, picksLocked: false, allowMultipleEntries, openPool, message: newContestMessage.trim() });
  };

  const joinContest = async () => {
    setError("");
    const q = query(collection(db, "contests"), where("joinCode", "==", joinCode.toUpperCase().trim()));
    const snap = await getDocs(q);
    if (snap.empty) { setError("No pool found with that code."); return; }
    const contestDoc = snap.docs[0];
    const contestData = { id: contestDoc.id, ...contestDoc.data() };
    if (!contestData.members.includes(user.uid)) {
      await updateDoc(doc(db, "contests", contestDoc.id), { members: arrayUnion(user.uid) });
    }
    try { sessionStorage.removeItem("pendingJoinCode"); } catch (e) {}
    onEnterContest(contestData);
  };

  return (
    <div className="animate-fade-in">
      {/* Hero banner with logo */}
      <div style={{
        background: "linear-gradient(135deg, #0d1f3c 0%, #112240 50%, #0d1f3c 100%)",
        border: "1px solid rgba(255,215,0,0.15)",
        borderRadius: 16, marginBottom: 24, overflow: "hidden",
        position: "relative"
      }}>
        {/* Field line decoration */}
        <div style={{ position: "absolute", inset: 0, opacity: 0.03, backgroundImage: "repeating-linear-gradient(0deg, white 0px, white 1px, transparent 1px, transparent 30px), repeating-linear-gradient(90deg, white 0px, white 1px, transparent 1px, transparent 30px)", pointerEvents: "none" }} />

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "20px 28px", gap: 20, flexWrap: "wrap" }}>
          <div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 13, color: "rgba(255,255,255,0.4)", letterSpacing: 3, marginBottom: 4 }}>WELCOME BACK</div>
            <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 32, color: "#FFD700", letterSpacing: 2, lineHeight: 1 }}>
              {(user.displayName || "PLAYER").toUpperCase()}
            </div>
            <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 13, marginTop: 6, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ width: 6, height: 6, borderRadius: "50%", background: "#00C853", display: "inline-block" }}></span>
              World Cup kicks off June 11, 2026 — Get your picks in!
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
              <button className="btn btn-primary btn-sm" onClick={() => setTab("create")}>+ Create Pool</button>
              <button className="btn btn-outline btn-sm" onClick={() => setTab("join")}>Join Pool</button>
            </div>
          </div>
          <img src={logo} alt="World Cup Pool 2026" style={{ height: 160, width: "auto", objectFit: "contain", filter: "drop-shadow(0 4px 20px rgba(0,0,0,0.6))", flexShrink: 0 }} />
        </div>
      </div>

      {/* Tab nav */}
      <div className="nav-tabs">
        <button className={"nav-tab" + (tab === "my" ? " active" : "")} onClick={() => setTab("my")}>My Pools</button>
        <button className={"nav-tab" + (tab === "create" ? " active" : "")} onClick={() => setTab("create")}>Create Pool</button>
        <button className={"nav-tab" + (tab === "join" ? " active" : "")} onClick={() => setTab("join")}>Join Pool</button>
      </div>

      {tab === "my" && (
        <div>
          {loading && <p style={{ color: "rgba(255,255,255,0.4)", textAlign: "center", padding: 32 }}>Loading...</p>}
          {!loading && myContests.length === 0 && (
            <div style={{ textAlign: "center", padding: "40px 20px", color: "rgba(255,255,255,0.4)" }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>🏆</div>
              <p>You haven't joined any pools yet.</p>
              <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 16 }}>
                <button className="btn btn-primary" onClick={() => setTab("create")}>Create a Pool</button>
                <button className="btn btn-outline" onClick={() => setTab("join")}>Join a Pool</button>
              </div>
            </div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {myContests.map((c) => (
              <div key={c.id} className="card" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 18px", borderLeft: "4px solid #FFD700" }}>
                <div>
                  <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 20, letterSpacing: 1 }}>{c.name}</div>
                  <div style={{ fontSize: 12, color: "rgba(255,255,255,0.4)", marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    {c.openPool !== false && <span>Code: <span style={{ color: "#FFD700", letterSpacing: 2, fontWeight: 700 }}>{c.joinCode}</span></span>}
                    {c.picksLocked && <span className="badge badge-red">LOCKED</span>}
                    {c.allowMultipleEntries === false && <span className="badge badge-gray">1 ENTRY</span>}
                    {c.openPool === false && <span className="badge badge-gray">CLOSED</span>}
                  </div>
                </div>
                <button className="btn btn-primary btn-sm" onClick={() => onEnterContest(c)}>Enter →</button>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "create" && (
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#FFD700", letterSpacing: 2, marginBottom: 16 }}>CREATE A NEW POOL</div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 6 }}>POOL NAME</label>
            <input value={newContestName} onChange={(e) => setNewContestName(e.target.value)} placeholder="e.g. Office Pool 2026" className="input" />
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 6 }}>
              POOL MESSAGE <span style={{ color: "rgba(255,255,255,0.2)", fontWeight: 400 }}>(optional)</span>
            </label>
            <textarea value={newContestMessage} onChange={(e) => setNewContestMessage(e.target.value)}
              placeholder="e.g. Buy-in is $20 — send to @yourvenmo. Winner takes all!"
              className="input" maxLength={500} style={{ minHeight: 80, resize: "vertical", lineHeight: 1.5 }} />
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.2)", textAlign: "right", marginTop: 4 }}>{newContestMessage.length}/500</div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: 1, marginBottom: 4 }}>POOL SETTINGS</div>
            <Toggle label="Multiple Entries" description="Allow players to submit more than one set of picks." value={allowMultipleEntries} onChange={setAllowMultipleEntries} />
            <Toggle label="Open Pool" description="Members can see the join code and invite others. Turn off to restrict invites to you only." value={openPool} onChange={setOpenPool} />
          </div>
          <button onClick={createContest} className="btn btn-primary btn-full btn-lg" style={{ borderRadius: 10 }}>⚽ Create Pool</button>
        </div>
      )}

      {tab === "join" && (
        <div className="card" style={{ padding: 24 }}>
          <div style={{ fontFamily: "'Bebas Neue', sans-serif", fontSize: 22, color: "#00C853", letterSpacing: 2, marginBottom: 16 }}>JOIN A POOL</div>
          <input value={joinCode} onChange={(e) => setJoinCode(e.target.value)} onKeyDown={(e) => e.key === "Enter" && joinContest()}
            placeholder="Enter 6-character join code" className="input" style={{ marginBottom: 12, letterSpacing: 3, textTransform: "uppercase" }} />
          {error && <p className="error-text" style={{ marginBottom: 10 }}>{error}</p>}
          <button onClick={joinContest} className="btn btn-success btn-full btn-lg" style={{ borderRadius: 10 }}>Join Pool →</button>
        </div>
      )}
    </div>
  );
}

export default ContestHome;
